import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Divider,
    OSKIcon,
    OSKThemeType,
    SidePanel,
    SidePanelHandle,
    SidePanelProps,
    Typography,
} from 'oskcomponents';
import { useDisableFeatureOnMouseOver, useMap } from '~/hooks';
import { RootState } from '~/redux/store';
import { connect, useDispatch } from 'react-redux';
import SearchDataCollectPreview from '~/organisms/SearchDataCollectPreview';
import { setWizardFlow } from '~/redux/modules/data/cart';
import ArchiveSearchCheckoutButton from './ArchiveSearchCheckoutButton';
import { Geometry, GeometryCollection, OSKGeoJson, OSKScene, noop } from 'oskcore';
import ToggleTimelineSliderButton from './ToggleTimelineSliderButton';
import { selectResults } from '~/redux/modules/data/search';
import { StacAsset } from 'stac-ts';
import { useTheme } from 'styled-components';
import { date_format_long, utc_time_format_long } from '~/utils';

type SearchSidePanelProps = {
    /** From redux, the search results */
    searchResults?: any;
    /** From redux, if an error was thrown by the search */
    searchError?: string;
    /** A flag representing whether there is a search in progress */
    isSearching?: boolean;
    /** A flag representing whether the cart features (including checkout) should be visible */
    cartEnabled?: boolean;
} & SidePanelProps;

const SearchSidePanel = ({ searchResults, searchError, isSearching, cartEnabled, ...props }: SearchSidePanelProps) => {
    const dispatch = useDispatch();
    const map = useMap();

    const [selectedScene, setSelectedScene] = useState<OSKScene | undefined>(undefined);
    const [searchCollapsible, setSearchCollapsible] = useState<boolean>(true);

    const searchPanelHandle = useRef<SidePanelHandle>(null);
    const searchPanelRef = useRef<HTMLDivElement>(null);

    const assetsPanelHandle = useRef<SidePanelHandle>(null);
    const assetsPanelRef = useRef<HTMLDivElement>(null);

    useDisableFeatureOnMouseOver(searchPanelRef, 'Zoom', true);
    useDisableFeatureOnMouseOver(searchPanelRef, 'Drag', true);
    useDisableFeatureOnMouseOver(searchPanelRef, 'Click', true);

    useDisableFeatureOnMouseOver(assetsPanelRef, 'Zoom', assetsPanelHandle.current?.getIsOpen() ?? false);
    useDisableFeatureOnMouseOver(assetsPanelRef, 'Drag', assetsPanelHandle.current?.getIsOpen() ?? false);
    useDisableFeatureOnMouseOver(assetsPanelRef, 'Click', assetsPanelHandle.current?.getIsOpen() ?? false);

    useEffect(() => {
        if (searchResults || searchError || isSearching) {
            searchPanelHandle.current?.open();
        }
    }, [searchResults, searchError, isSearching]);

    useEffect(() => {
        // If we ever start searching, close whatever asset panel is currently open
        if (isSearching) {
            assetsPanelHandle.current?.close();
            setSelectedScene(undefined);
        }
    }, [isSearching]);

    return (
        <Box
            style={{
                overflow: 'hidden',
                pointerEvents: 'auto',
            }}
            grow
        >
            <SidePanel
                collapsible={searchCollapsible}
                visible={false}
                panelId="search-default"
                style={{ height: '100%' }}
                ref={searchPanelHandle}
                width={400}
                {...props}
            >
                <Box ref={searchPanelRef} id="search-panel" col grow style={{ height: '100%' }}>
                    <Box style={{ justifyContent: 'space-between' }}>
                        <Box center="vertical">
                            <Typography variant="heading2">Search Results</Typography>
                        </Box>
                        <ToggleTimelineSliderButton />
                    </Box>

                    <Box
                        col
                        style={{
                            display: 'block',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            height: '100%',
                            paddingTop: '14px',
                        }}
                    >
                        <SearchDataCollectPreview
                            cartEnabled={cartEnabled}
                            onFootprintClick={(scene: OSKScene) => {
                                const centroid = OSKGeoJson.fromAPIGeometry(
                                    scene.geometry as Geometry | GeometryCollection,
                                ).getCentroid();
                                map.panTo(centroid);
                            }}
                            onAssetsPanelOpen={async (scene: OSKScene) => {
                                await assetsPanelHandle.current?.close();

                                /* Only open the panel again if it's for a new scene,
                                 otherwise just keep the panel closed and
                                 clear out the previous selection */
                                if (selectedScene !== scene) {
                                    searchPanelHandle.current?.openSub(assetsPanelHandle);
                                    setSelectedScene(scene);
                                    setSearchCollapsible(false);
                                } else {
                                    setSelectedScene(undefined);
                                }
                            }}
                        />
                    </Box>

                    {/* DO NOT REMOVE this box!!! It adds padding. */}
                    <Box h={120} />

                    {cartEnabled && (
                        <Box
                            h={80}
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                backgroundColor: 'white',
                                width: '100%',
                                padding: '4px 8px',
                                boxShadow: '0px -1px 17px rgba(0, 0, 0, 0.19)',
                            }}
                            center="all"
                        >
                            <ArchiveSearchCheckoutButton
                                onClick={() => {
                                    dispatch(setWizardFlow('archive'));
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </SidePanel>
            <SidePanel
                panelId="item-assets"
                ref={assetsPanelHandle}
                visible={false}
                expandable={false}
                onClose={() => {
                    setSearchCollapsible(true);
                    return Promise.resolve();
                }}
            >
                <Box col ref={assetsPanelRef} id="item-assets-panel" grow>
                    <Typography variant="heading3" style={{}}>
                        Item Assets
                    </Typography>
                    <Typography variant="body3" style={{}}>
                        {date_format_long(selectedScene?.datetime)} - {utc_time_format_long(selectedScene?.datetime)}
                    </Typography>
                    <Divider style={{ margin: '16px 0px' }} />
                    <Typography variant="heading3" style={{ marginBottom: '4px' }}>
                        Level 1A
                    </Typography>
                    {selectedScene &&
                        Object.values(selectedScene.assets ?? []).map((asset, idx) => (
                            <AssetDownload key={`asset-${idx}`} asset={asset} />
                        ))}
                </Box>
            </SidePanel>
        </Box>
    );
};

const AssetDownload = ({ asset }: { asset: StacAsset }) => {
    const theme = useTheme() as OSKThemeType;

    return (
        <Box
            style={{
                border: `1px solid ${theme.colors.black200}`,
                borderRadius: '16deg',
                backgroundColor: 'white',
                justifyContent: 'space-between',
                margin: '4px',
                padding: '14px 14px',
            }}
            center="vertical"
        >
            <Box col>
                <Typography variant="body3" strong style={{ width: '250px' }}>
                    {asset.title}
                </Typography>
                <Typography variant="body3">{asset.type}</Typography>
            </Box>
            <OSKIcon
                code="download-modern"
                fill={theme.colors.black600}
                hoverFill={theme.colors.black200}
                p={12}
                onClick={() => {
                    window.open(asset.href);
                }}
            />
        </Box>
    );
};

const mapStateToProps = (state: RootState) => {
    const results = selectResults(state);
    const { isSearching, searchError } = state.data.search;

    return {
        searchResults: results,
        searchError,
        isSearching,
    };
};

export { SearchSidePanel };
export default connect(mapStateToProps, noop)(SearchSidePanel);
